import { Container, Stack, Typography, Snackbar, Alert } from '@mui/material';
import { Routes, ZoomLevels } from 'enums';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import LeadListUploadDialog from 'features/lead-list-research/components/lead-list-upload-dialog';
import GenerateLeadListTab from 'features/lead-list-research/lead-list-research-generate/generate-lead-list-tab';
import LeadListFormCreation from 'features/lead-list-research/lead-list-research-generate/lead-list-form-creation';
import LeadListUploadCreation from 'features/lead-list-research/lead-list-research-generate/lead-list-upload-creation';
import { parse } from 'papaparse';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import * as XLSX from 'xlsx';
import { LeadListGenerateParams, useGenerateLeadList, useUploadLeadList } from '../services/lead-list-research.service';

const LeadListResearchGenerate: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);
  const [prompt, setPrompt] = useState('');
  const [product, setProduct] = useState('');
  const [region, setRegion] = useState('');
  const [regionSplitInstructions, setRegionSplitInstructions] = useState('');
  const [subsearchInstructions, setSubsearchInstructions] = useState('');
  const [peopleSearchInstructions, setPeopleSearchInstructions] = useState('');

  // Mode toggle for generate vs. upload
  const [mode, setMode] = useState<'generate' | 'upload'>('generate');
  const [listName, setListName] = useState('');

  // File upload & mapping dialog state
  interface FileUploadDialogState {
    open: boolean;
    data: string[][];
    columns: { text: string; type: string }[];
    fileNames: string[];
    hasTableHeading: boolean;
  }
  const initialDialogState: FileUploadDialogState = {
    open: false,
    data: [],
    columns: [],
    fileNames: [],
    hasTableHeading: true,
  };
  const [fileUploadDialog, setFileUploadDialog] = useState<FileUploadDialogState>(initialDialogState);

  const { mutate: generateLeadList, isPending: isGenerating } = useGenerateLeadList();
  const { mutate: uploadLeadList, isPending: isUploading } = useUploadLeadList();
  const [previewRows, setPreviewRows] = useState<any[]>([]);

  // Success snackbar state for uploads / enrichment start
  const [successSnackbar, setSuccessSnackbar] = useState('');

  useEffect(() => {
    // Set the active tab to TOOL when the component mounts
    setActiveTab(NavigationStates.TOOL);

    // Parse URL search params to get prefilled values
    const searchParams = new URLSearchParams(location.search);
    const promptParam = searchParams.get('prompt');
    const productParam = searchParams.get('product');
    const regionParam = searchParams.get('region');

    if (promptParam) {
      setPrompt(promptParam);
    }

    if (productParam) {
      setProduct(productParam);
    }

    if (regionParam) {
      setRegion(regionParam);
    }
  }, [location.search]);

  const handlePromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(event.target.value);
  };

  const handleGenerateLeadList = async () => {
    if (!prompt.trim() && (!product.trim() || !region.trim())) return;

    // Use the imported interface for proper typing
    const params: LeadListGenerateParams = {
      product: product || 'General',
      region: region || 'Global',
      autoSave: true,
    };

    // If prompt is provided, add it to the request payload
    if (prompt.trim()) {
      params.prompt = prompt.trim();
    }

    if (regionSplitInstructions.trim()) {
      params.regionSplitInstructions = regionSplitInstructions.trim();
    }

    if (subsearchInstructions.trim()) {
      params.subsearchInstructions = subsearchInstructions.trim();
    }

    if (peopleSearchInstructions.trim()) {
      params.peopleSearchInstructions = peopleSearchInstructions.trim();
    }

    generateLeadList(params, {
      onSuccess: (response: any) => {
        const msg = response?.message || 'Lead list generation started';
        setSuccessSnackbar(msg);
        setTimeout(() => navigate(Routes.LEAD_LIST_STORAGE), 3000);
      },
      onError: (error: unknown) => {
        console.error('Error generating lead list:', error);
        alert('Failed to generate lead list. Please try again.');
      },
    });
  };

  // Parse files (Excel or CSV) and open mapping dialog
  const readFiles = (files: File[]) => {
    files.forEach((file) => {
      const reader = new FileReader();
      if (file.name.match(/\.xlsx?$|\.xls$/i)) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: 'array' });
          const sheet = wb.SheetNames[0];
          const raw: string[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheet], { header: 1 });
          handleOpenFileUploadDialog(raw, file.name);
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.match(/\.csv$/i)) {
        reader.onload = (e: any) => {
          const text: string = e.target.result;
          const res = parse(text, { header: false });
          if (res.data) handleOpenFileUploadDialog(res.data as string[][], file.name);
        };
        reader.readAsText(file);
      } else {
        console.warn('Unsupported file type');
      }
    });
  };

  const onFileDropHandler = useCallback((files: File[]) => {
    if (files.length) readFiles(files);
  }, []);

  const handleOpenFileUploadDialog = (data: string[][], fileName: string) => {
    const headerRow = data[0] || [];
    const cols = headerRow.map((h: string) => ({ text: h, type: '' }));
    setFileUploadDialog({ open: true, data, columns: cols, fileNames: [fileName], hasTableHeading: true });
  };

  const handleCloseFileUploadDialog = () => setFileUploadDialog({ open: false, data: [], columns: [], fileNames: [], hasTableHeading: true });

  const handleAcceptColumns = () => {
    const { data, columns, hasTableHeading } = fileUploadDialog;
    const rows = hasTableHeading ? data.slice(1) : data;
    const mapped = rows.map((row) => {
      const contact: any = {};
      const company: any = {};
      columns.forEach((col, idx) => {
        const val = row[idx];
        switch (col.type) {
          case FileUploadMatchColumns.FIRST_NAME:
            contact.firstName = val;
            break;
          case FileUploadMatchColumns.LAST_NAME:
            contact.lastName = val;
            break;
          case FileUploadMatchColumns.EMAIL:
            contact.email = val;
            break;
          case FileUploadMatchColumns.PHONE_NUMBER:
            contact.phone = val;
            break;
          case FileUploadMatchColumns.COMPANY:
            company.name = val;
            break;
          case FileUploadMatchColumns.ROLE:
          case FileUploadMatchColumns.POSITION:
            contact.role = val;
            break;
        }
      });

      // Ensure email and phone fields exist even if they were not provided in the upload
      if (!('email' in contact)) {
        contact.email = '';
      }
      if (!('phone' in contact)) {
        contact.phone = '';
      }
      return { contact, company };
    });
    setPreviewRows(mapped);
    handleCloseFileUploadDialog();
  };

  const handleStartEnrichment = () => {
    uploadLeadList(
      { name: listName.trim(), product: product || 'General', region: region || 'Global', leadData: previewRows },
      {
        onSuccess: (response: any) => {
          // Show success message returned by backend (fallback to generic)
          const msg = response?.message || 'Enrichment started';
          setSuccessSnackbar(msg);
          // Redirect to Lead List overview after 3 seconds
          setTimeout(() => navigate(Routes.LEAD_LIST_STORAGE), 3000);
        },
        onError: (err: unknown) => {
          console.error('Enrichment failed', err);
          alert('Failed to enrich list');
        },
      }
    );
  };

  return (
    <Container maxWidth="md">
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>{t('generateLeadList')}</Typography>
        </div>
        <div className={classes.form}>
          <Stack className={classes.formContent} gap={2}>
            {/* Toggle between generation and file upload modes */}
            <GenerateLeadListTab mode={mode} setMode={setMode} />

            {mode === 'generate' ? (
              <LeadListFormCreation
                prompt={prompt}
                handlePromptChange={handlePromptChange}
                product={product}
                setProduct={setProduct}
                region={region}
                setRegion={setRegion}
                regionSplitInstructions={regionSplitInstructions}
                setRegionSplitInstructions={setRegionSplitInstructions}
                subsearchInstructions={subsearchInstructions}
                setSubsearchInstructions={setSubsearchInstructions}
                peopleSearchInstructions={peopleSearchInstructions}
                setPeopleSearchInstructions={setPeopleSearchInstructions}
                handleGenerateLeadList={handleGenerateLeadList}
                isGenerating={isGenerating}
              />
            ) : (
              <LeadListUploadCreation
                listName={listName}
                setListName={setListName}
                onFileDropHandler={onFileDropHandler}
                handleStartEnrichment={handleStartEnrichment}
                isUploading={isUploading}
                previewRows={previewRows}
              />
            )}
          </Stack>
        </div>
      </div>

      {/* then mapping dialog */}
      <LeadListUploadDialog
        open={fileUploadDialog.open}
        columns={fileUploadDialog.columns}
        data={fileUploadDialog.data}
        fileName={fileUploadDialog.fileNames.join(', ')}
        hasTableHeading={fileUploadDialog.hasTableHeading}
        handleColumnTypeChange={(i) => (e) =>
          setFileUploadDialog((prev) => {
            const newCols = [...prev.columns];
            newCols[i].type = e.target.value;
            return { ...prev, columns: newCols };
          })}
        handleHasTableHeadingChange={(e) => setFileUploadDialog((prev) => ({ ...prev, hasTableHeading: e.target.checked }))}
        handleClose={handleCloseFileUploadDialog}
        handleAcceptColumns={handleAcceptColumns}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar('')} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  header: {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  form: {
    padding: '16px',
    height: '100%',
    overflow: 'auto',
  },
  formContent: {
    padding: '16px',
    background: '#FFF',
    borderRadius: '8px',
    height: '100%',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
}));

export default LeadListResearchGenerate;
